import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { TrendingUp, TrendingDown, AlertTriangle, ArrowRight, Loader2, Zap, Package } from "lucide-react";
import { toast } from "sonner";

const fmtMoney = (v) =>
  v == null ? "—" : `${Number(v).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR"); } catch { return iso; }
};

export const SupplierAnalyticsPage = ({ embedded = false } = {}) => {
  const [threshold, setThreshold] = useState(15);
  const [minSavings, setMinSavings] = useState(5);
  const [alerts, setAlerts] = useState(null);
  const [opportunities, setOpportunities] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ar, or_] = await Promise.all([
        api.getPriceAlerts(threshold),
        api.getSavingsOpportunities(minSavings),
      ]);
      setAlerts(ar.data);
      setOpportunities(or_.data);
    } catch {
      toast.error("Erreur chargement analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={embedded ? "space-y-6" : "space-y-6 p-6"}>
      {!embedded && (
        <div>
          <h1 className="text-3xl font-heading">Analyse des prix fournisseurs</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Détection des hausses de prix anormales et opportunités d'économie multi-fournisseurs.
          </p>
        </div>
      )}

      <Tabs defaultValue="alerts" className="space-y-4">
        <TabsList>
          <TabsTrigger value="alerts" data-testid="tab-price-alerts">
            <AlertTriangle className="h-4 w-4 mr-2" />
            Hausses de prix {alerts && <Badge variant="secondary" className="ml-2">{alerts.count}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="opportunities" data-testid="tab-savings">
            <Zap className="h-4 w-4 mr-2" />
            Opportunités d'économie {opportunities && <Badge variant="secondary" className="ml-2">{opportunities.count}</Badge>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="alerts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-red-600" />
                  Hausses anormales
                </span>
                <div className="flex items-center gap-2">
                  <Label htmlFor="threshold" className="text-xs">Seuil d'alerte</Label>
                  <Input
                    id="threshold"
                    type="number"
                    value={threshold}
                    onChange={(e) => setThreshold(Number(e.target.value) || 15)}
                    className="w-20 h-8"
                    min={1}
                    max={100}
                    data-testid="threshold-input"
                  />
                  <span className="text-xs text-muted-foreground">%</span>
                  <Button size="sm" onClick={fetchData} disabled={loading} data-testid="refresh-alerts-btn">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Rafraîchir"}
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="py-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
              ) : !alerts || alerts.count === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center" data-testid="no-alerts">
                  Aucune hausse anormale détectée (≥ {threshold}%).
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left p-2">Produit</th>
                        <th className="text-left p-2">Fournisseur</th>
                        <th className="text-right p-2">Prix précédent</th>
                        <th className="text-right p-2">Prix actuel</th>
                        <th className="text-right p-2">Écart</th>
                      </tr>
                    </thead>
                    <tbody>
                      {alerts.alerts.map((a) => <AlertRow key={a.product_id} a={a} />)}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="opportunities" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <TrendingDown className="h-5 w-5 text-emerald-600" />
                  Opportunités d'économie
                </span>
                <div className="flex items-center gap-2">
                  <Label htmlFor="min-savings" className="text-xs">Économie min.</Label>
                  <Input
                    id="min-savings"
                    type="number"
                    value={minSavings}
                    onChange={(e) => setMinSavings(Number(e.target.value) || 5)}
                    className="w-20 h-8"
                    min={1}
                    max={100}
                    data-testid="min-savings-input"
                  />
                  <span className="text-xs text-muted-foreground">%</span>
                  <Button size="sm" onClick={fetchData} disabled={loading} data-testid="refresh-opportunities-btn">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Rafraîchir"}
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {opportunities && opportunities.count > 0 && (
                <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded">
                  <div className="text-xs text-emerald-700">Économie totale potentielle (par unité vendue)</div>
                  <div className="text-2xl font-heading font-bold text-emerald-800" data-testid="total-savings">
                    {fmtMoney(opportunities.total_potential_savings)}
                  </div>
                </div>
              )}
              {loading ? (
                <div className="py-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
              ) : !opportunities || opportunities.count === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center" data-testid="no-opportunities">
                  Aucune opportunité d'économie ≥ {minSavings}%.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left p-2">Produit</th>
                        <th className="text-center p-2">Changement</th>
                        <th className="text-right p-2">Prix actuel</th>
                        <th className="text-right p-2">Prix alternatif</th>
                        <th className="text-right p-2">Économie</th>
                      </tr>
                    </thead>
                    <tbody>
                      {opportunities.opportunities.map((o) => <OppRow key={o.product_id} o={o} />)}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SupplierAnalyticsPage;

const AlertRow = ({ a }) => (
  <tr className="border-b hover:bg-slate-50" data-testid={`alert-row-${a.product_id}`}>
    <td className="p-2">
      <div className="font-medium">{a.product_name}</div>
      {a.product_reference && (
        <div className="font-mono text-xs text-muted-foreground">{a.product_reference}</div>
      )}
    </td>
    <td className="p-2">{a.supplier_name || "—"}</td>
    <td className="p-2 text-right">
      <div className="font-mono">{fmtMoney(a.previous_amount)}</div>
      <div className="text-[10px] text-muted-foreground">{fmtDate(a.previous_date)}</div>
    </td>
    <td className="p-2 text-right">
      <div className="font-mono font-semibold">{fmtMoney(a.current_amount)}</div>
      <div className="text-[10px] text-muted-foreground">{fmtDate(a.current_date)}</div>
    </td>
    <td className="p-2 text-right">
      <Badge className={a.delta_pct >= 25 ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}>
        +{a.delta_pct}%
      </Badge>
      <div className="text-[10px] text-muted-foreground font-mono">
        +{fmtMoney(a.delta_amount)}
      </div>
    </td>
  </tr>
);

const OppRow = ({ o }) => (
  <tr className="border-b hover:bg-slate-50" data-testid={`opp-row-${o.product_id}`}>
    <td className="p-2">
      <div className="font-medium flex items-center gap-1">
        <Package className="h-3 w-3 text-muted-foreground" />
        {o.product_name}
      </div>
      {o.product_reference && (
        <div className="font-mono text-xs text-muted-foreground">{o.product_reference}</div>
      )}
    </td>
    <td className="p-2 text-center">
      <div className="flex items-center gap-1 text-xs justify-center">
        <Badge variant="outline" className="text-[10px]">{o.current_supplier}</Badge>
        <ArrowRight className="h-3 w-3" />
        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px]">
          {o.alternative_supplier}
        </Badge>
      </div>
    </td>
    <td className="p-2 text-right font-mono">{fmtMoney(o.current_amount)} / {o.unit}</td>
    <td className="p-2 text-right font-mono text-emerald-700 font-semibold">
      {fmtMoney(o.alternative_amount)} / {o.unit}
    </td>
    <td className="p-2 text-right">
      <Badge className="bg-emerald-100 text-emerald-800">-{o.savings_pct}%</Badge>
      <div className="text-[10px] text-emerald-700 font-mono">
        -{fmtMoney(o.savings_per_unit)} / {o.unit}
      </div>
    </td>
  </tr>
);
